import React, { useEffect, useState } from 'react';
import { FiPlus, FiUserCheck, FiUserX, FiMail } from 'react-icons/fi';
import { getUsers, inviteUser, deactivateUser, activateUser, changeUserRole } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

const Users = () => {
  const { t } = useLanguage();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'operator' });
  const [inviteResult, setInviteResult] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await inviteUser(inviteData);
      setInviteResult(res.data.token);
    } catch (err) { console.error(err); }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      if (isActive) { await deactivateUser(id); } else { await activateUser(id); }
      fetchUsers();
    } catch (err) { console.error(err); }
  };

  const handleRoleChange = async (id: string, role: string) => {
    try { await changeUserRole(id, role); fetchUsers(); } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-400">Loading...</div></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-white text-3xl font-bold">{t('users')}</h1>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
          <FiPlus />{t('invite')}
        </button>
      </div>

      <div className="bg-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left text-slate-400 font-medium px-6 py-4">User</th>
              <th className="text-left text-slate-400 font-medium px-6 py-4">{t('role')}</th>
              <th className="text-left text-slate-400 font-medium px-6 py-4">{t('lastLogin')}</th>
              <th className="text-left text-slate-400 font-medium px-6 py-4">{t('status')}</th>
              <th className="text-left text-slate-400 font-medium px-6 py-4">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                <td className="px-6 py-4">
                  <p className="text-white font-medium">{user.name}</p>
                  <p className="text-slate-400 text-sm">{user.email}</p>
                </td>
                <td className="px-6 py-4">
                  <select value={user.role} onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className="bg-slate-700 text-white px-3 py-1 rounded-lg text-sm">
                    <option value="superadmin">{t('superadmin')}</option>
                    <option value="admin">{t('admin')}</option>
                    <option value="operator">{t('operator')}</option>
                    <option value="viewer">{t('viewer')}</option>
                  </select>
                </td>
                <td className="px-6 py-4 text-slate-400 text-sm">
                  {user.last_login ? new Date(user.last_login).toLocaleString() : t('never')}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-sm ${user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {user.is_active ? t('active') : t('deactivated')}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <button onClick={() => handleToggleActive(user.id, user.is_active)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                      user.is_active ? 'bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white' : 'bg-green-600/20 hover:bg-green-600 text-green-400 hover:text-white'
                    }`}>
                    {user.is_active ? <><FiUserX />{t('deactivate')}</> : <><FiUserCheck />{t('activate')}</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4">
            {inviteResult ? (
              <div>
                <h3 className="text-white text-xl font-semibold mb-4">{t('inviteCreated')}</h3>
                <p className="text-slate-400 mb-4">{t('inviteToken')}</p>
                <div className="bg-slate-700 rounded-xl p-4">
                  <p className="text-green-400 font-mono text-sm break-all">{inviteResult}</p>
                </div>
                <button onClick={() => { setShowModal(false); setInviteResult(null); setInviteData({ email: '', role: 'operator' }); }}
                  className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold">{t('close')}</button>
              </div>
            ) : (
              <div>
                <h3 className="text-white text-xl font-semibold mb-6">{t('inviteUser')}</h3>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div>
                    <label className="text-slate-400 text-sm mb-2 block">{t('email')}</label>
                    <div className="relative">
                      <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="email" value={inviteData.email} onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                        className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                    </div>
                  </div>
                  <div>
                    <label className="text-slate-400 text-sm mb-2 block">{t('role')}</label>
                    <select value={inviteData.role} onChange={(e) => setInviteData({...inviteData, role: e.target.value})}
                      className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="admin">{t('admin')}</option>
                      <option value="operator">{t('operator')}</option>
                      <option value="viewer">{t('viewer')}</option>
                    </select>
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold">{t('invite')}</button>
                    <button type="button" onClick={() => setShowModal(false)} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold">{t('cancel')}</button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
